import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { api, buildCatalogueUrl } from "../lib/api";
import { Download, Edit, Trash2, Plus, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "../components/ImageUploader";

const CATEGORIES = [
  "Designer Suits",
  "Co-Ord Sets",
  "Kaftans",
  "Kurta Sets",
  "Indo-Western Wear",
  "Festive Collections",
];

const empty = {
  name: "", category: CATEGORIES[0], description: "", image_url: "",
  moq: 10, price: 0, fabric: "", sizes: "Free Size", active: true, featured: false,
};

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // product or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [galleryMode, setGalleryMode] = useState("curated");
  const [savingMode, setSavingMode] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data }, { data: settings }] = await Promise.all([
        api.get("/admin/products"),
        api.get("/site/settings"),
      ]);
      setItems(data || []);
      setGalleryMode(settings?.gallery_mode || "curated");
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setBusy(false);
    }
  };

  const updateGalleryMode = async (mode) => {
    setSavingMode(true);
    const prev = galleryMode;
    setGalleryMode(mode);
    try {
      await api.put("/admin/site/settings", { gallery_mode: mode });
      toast.success(
        mode === "featured"
          ? "Home gallery now shows featured products"
          : "Home gallery restored to curated tiles"
      );
    } catch (e) {
      setGalleryMode(prev);
      toast.error("Failed to update home gallery mode");
    } finally {
      setSavingMode(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (!loading && !user) return <Navigate to="/admin/login" replace />;
  if (loading) {
    return <div className="p-16 text-center font-serif-display text-2xl italic">Loading…</div>;
  }

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, category: p.category, description: p.description || "",
      image_url: p.image_url || "", moq: p.moq, price: p.price,
      fabric: p.fabric || "", sizes: p.sizes || "Free Size", active: !!p.active, featured: !!p.featured,
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, moq: Number(form.moq) || 0, price: Number(form.price) || 0 };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Added");
      }
      setShowForm(false); setEditing(null); setForm(empty);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F4F7FE]">
      <div className="border-b border-[#DCE4F2] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="overline">Admin Console</div>
            <h1 className="font-serif-display text-3xl md:text-4xl mt-1">Product Catalogue</h1>
          </div>
          <div className="flex gap-3 flex-wrap">
            <a href={buildCatalogueUrl()} target="_blank" rel="noreferrer" data-testid="admin-download-pdf" className="btn-secondary !py-3 !px-5">
              <Download size={14} strokeWidth={1.5} /> Download PDF
            </a>
            <button data-testid="admin-new-product" onClick={openNew} className="btn-primary !py-3 !px-5">
              <Plus size={14} strokeWidth={1.5} /> New Product
            </button>
            <button data-testid="admin-logout" onClick={doLogout} className="btn-secondary !py-3 !px-5">
              <LogOut size={14} strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-10">
        {/* ============== HOME GALLERY MODE TOGGLE ============== */}
        <div
          data-testid="gallery-mode-card"
          className="border border-[#DCE4F2] bg-white p-6 md:p-7 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
        >
          <div className="max-w-2xl">
            <div className="overline">Home Gallery Source</div>
            <h2 className="font-serif-display text-2xl md:text-3xl mt-2 text-[#0F1245]">
              {galleryMode === "featured" ? (
                <>Featured <span className="italic text-[#4A3FCF]">products</span></>
              ) : (
                <>Curated <span className="italic text-[#4A3FCF]">tiles</span></>
              )}
            </h2>
            <p className="text-[13px] text-[#5A5F7A] mt-2 leading-relaxed">
              {galleryMode === "featured"
                ? "The public home page shows products you mark as ‘Feature on home gallery’. Falls back to curated tiles if none are featured."
                : "The public home page shows the 6 hand-curated tiles built into the site. Admin ‘Featured’ flag is ignored."}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              data-testid="gallery-mode-curated"
              disabled={savingMode || galleryMode === "curated"}
              onClick={() => updateGalleryMode("curated")}
              className={`px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium border transition-colors ${
                galleryMode === "curated"
                  ? "bg-[#0F1245] text-white border-[#0F1245]"
                  : "bg-white text-[#0F1245] border-[#DCE4F2] hover:border-[#0F1245]"
              } ${savingMode ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              Curated
            </button>
            <button
              type="button"
              data-testid="gallery-mode-featured"
              disabled={savingMode || galleryMode === "featured"}
              onClick={() => updateGalleryMode("featured")}
              className={`px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium border transition-colors ${
                galleryMode === "featured"
                  ? "bg-[#0F1245] text-white border-[#0F1245]"
                  : "bg-white text-[#0F1245] border-[#DCE4F2] hover:border-[#0F1245]"
              } ${savingMode ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              Featured Products
            </button>
          </div>
        </div>

        <div className="border border-[#DCE4F2] bg-white">
          <div className="grid grid-cols-12 px-6 py-4 border-b border-[#DCE4F2] overline">
            <div className="col-span-4">Product</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1">MOQ</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-1">Active</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {busy && <div className="p-10 text-center text-[#5A5F7A]">Loading…</div>}
          {!busy && items.length === 0 && (
            <div className="p-10 text-center text-[#5A5F7A]">
              No products yet. <button onClick={openNew} className="underline text-[#4A3FCF]">Add the first piece</button>.
            </div>
          )}
          {items.map((p, i) => (
            <div key={p.id} data-testid={`admin-row-${i}`} className="grid grid-cols-12 px-6 py-5 border-b border-[#DCE4F2] items-center text-[14px]">
              <div className="col-span-4">
                <div className="font-serif-display text-lg leading-tight">{p.name}</div>
                {p.fabric && <div className="text-[12px] text-[#5A5F7A] mt-1">{p.fabric}</div>}
              </div>
              <div className="col-span-2 text-[13px]">{p.category}</div>
              <div className="col-span-1 text-[13px]">{p.moq}</div>
              <div className="col-span-2 text-[13px]">₹ {Number(p.price || 0).toLocaleString("en-IN")}</div>
              <div className="col-span-1">
                <span className={`inline-block w-2 h-2 ${p.active ? "bg-[#4A3FCF]" : "bg-[#aaa]"}`} />
                <span className="ml-2 text-[12px] uppercase tracking-wider">{p.active ? "Live" : "Off"}</span>
                {p.featured && <span className="ml-2 text-[10px] tracking-widest uppercase text-[#4A3FCF] font-semibold">★ Home</span>}
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <button data-testid={`admin-edit-${i}`} onClick={() => openEdit(p)} className="border border-[#DCE4F2] p-2 hover:border-[#0F1245]" aria-label="Edit">
                  <Edit size={14} />
                </button>
                <button data-testid={`admin-delete-${i}`} onClick={() => remove(p)} className="border border-[#DCE4F2] p-2 hover:border-[#0F1245]" aria-label="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showForm && (
        <div data-testid="admin-form-modal" className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-[#F4F7FE] border border-[#DCE4F2] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">{editing ? "Edit Product" : "New Product"}</div>
                <h2 className="font-serif-display text-3xl mt-1">{editing ? editing.name : "Add to catalogue"}</h2>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="p-2" aria-label="Close" data-testid="admin-form-close">
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-8">
              <div className="md:col-span-2">
                <label className="overline">Name *</label>
                <input data-testid="admin-form-name" required className="line-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="overline">Category *</label>
                <select data-testid="admin-form-category" required className="line-input bg-transparent" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="overline">Product Image</label>
                <ImageUploader
                  value={form.image_url}
                  onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                />
              </div>
              <div>
                <label className="overline">MOQ</label>
                <input data-testid="admin-form-moq" type="number" min={1} className="line-input" value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} />
              </div>
              <div>
                <label className="overline">Price (₹ per piece)</label>
                <input data-testid="admin-form-price" type="number" min={0} step="0.01" className="line-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <label className="overline">Fabric</label>
                <input data-testid="admin-form-fabric" className="line-input" value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} placeholder="Georgette · Silk…" />
              </div>
              <div>
                <label className="overline">Sizes</label>
                <input data-testid="admin-form-sizes" className="line-input" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="M / L / XL…" />
              </div>
              <div className="md:col-span-2">
                <label className="overline">Description</label>
                <textarea data-testid="admin-form-description" className="line-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex flex-wrap items-center gap-6 pt-4">
                <label className="inline-flex items-center gap-3 text-[13px] tracking-widest uppercase">
                  <input id="active" data-testid="admin-form-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  Show on public collections
                </label>
                <label className="inline-flex items-center gap-3 text-[13px] tracking-widest uppercase">
                  <input id="featured" data-testid="admin-form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                  Feature on home gallery
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary !py-3 !px-5">Cancel</button>
              <button type="submit" data-testid="admin-form-submit" className="btn-primary !py-3 !px-5">{editing ? "Save changes" : "Add product"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
